import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  alpha,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip as MuiTooltip,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  CalendarToday,
  Refresh,
  Download,
  Visibility,
  LocalHospital,
  Medication,
  AttachMoney,
  DateRange,
  FilterList,
  Print,
  PictureAsPdf,
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';

// Import your logo (adjust the path based on your project structure)
import logo from '../assets/AuraLogo.png'; // Update this path to your logo location

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#9c27b0", "#d32f2f"];

// Helper function to format date as dd/MM/yyyy for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to parse dd/MM/yyyy back to yyyy-MM-dd for input value
const parseDisplayDateToInput = (displayDate) => {
  if (!displayDate) return '';
  const parts = displayDate.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month}-${day}`;
  }
  return '';
};

// Helper function to format date as dd/MM/yyyy
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Sri Lankan Rupee formatter
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value).replace('LKR', 'Rs.');
};

// Compact currency formatter for tables
const formatCompactCurrency = (value) => {
  if (value >= 1000000) {
    return `Rs. ${(value / 1000000).toFixed(2)}M`;
  } else if (value >= 1000) {
    return `Rs. ${(value / 1000).toFixed(1)}K`;
  }
  return `Rs. ${value}`;
};

const MetricCard = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%', bgcolor: color, color: 'white' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="body2" sx={{ opacity: 0.9 }} gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" component="div" fontWeight="bold">
            {formatCurrency(value)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', width: 45, height: 45 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const StatBadge = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="body2" color="textSecondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" sx={{ color }}>
      {value}
    </Typography>
  </Box>
);

export default function SalesReport() {
  const theme = useTheme();
  const reportRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 10);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  // Display values for the date inputs
  const [startDateDisplay, setStartDateDisplay] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 10);
    return formatDateForDisplay(date.toISOString());
  });
  const [endDateDisplay, setEndDateDisplay] = useState(() => {
    return formatDateForDisplay(new Date().toISOString());
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [logoBase64, setLogoBase64] = useState("");

  // Load logo and convert to base64
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(logo);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error loading logo:", error);
      }
    };
    loadLogo();
  }, []);

  useEffect(() => {
    fetchReport();
  }, []);

  const handleStartDateChange = (e) => {
    const inputValue = e.target.value;
    setStartDate(inputValue);
    setStartDateDisplay(formatDateForDisplay(inputValue));
  };

  const handleEndDateChange = (e) => {
    const inputValue = e.target.value;
    setEndDate(inputValue);
    setEndDateDisplay(formatDateForDisplay(inputValue));
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError("Please select both dates");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/Treatment/patient/record/by-date?startDate=${startDate}&endDate=${endDate}`
      );

      setData(response.data);
    } catch (err) {
      setError("Failed to load sales report");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = data.reduce(
    (acc, item) => {
      const drugAmount = item.Drugs?.reduce(
        (sum, drug) => sum + (drug.MDD_AMOUNT || 0),
        0
      ) || 0;
      
      acc.treatment += item.MTD_AMOUNT || 0;
      acc.drugs += drugAmount;
      acc.grandTotal += (item.MTD_AMOUNT || 0) + drugAmount;
      acc.count += 1;
      
      return acc;
    },
    { treatment: 0, drugs: 0, grandTotal: 0, count: 0 }
  );

  // Prepare Daily Chart Data with formatted dates
  const dailyData = data.reduce((acc, item) => {
    const date = formatDate(item.MTD_DATE);
    const drugAmount = item.Drugs?.reduce(
      (sum, drug) => sum + (drug.MDD_AMOUNT || 0),
      0
    ) || 0;

    const treatmentAmount = item.MTD_AMOUNT || 0;

    const existing = acc.find((d) => d.date === date);

    if (existing) {
      existing.treatment += treatmentAmount;
      existing.drugs += drugAmount;
      existing.total += (treatmentAmount + drugAmount);
    } else {
      acc.push({ 
        date, 
        treatment: treatmentAmount, 
        drugs: drugAmount,
        total: treatmentAmount + drugAmount
      });
    }

    return acc;
  }, []).sort((a, b) => {
    // Parse dd/MM/yyyy format for sorting
    const [aDay, aMonth, aYear] = a.date.split('/');
    const [bDay, bMonth, bYear] = b.date.split('/');
    return new Date(aYear, aMonth-1, aDay) - new Date(bYear, bMonth-1, bDay);
  });

  const pieData = [
    { name: "Treatment", value: totals.treatment },
    { name: "Services", value: totals.drugs },
  ];

  // Filter data for table
  const filteredData = data.filter(item => 
    item.MTD_PATIENT_CODE?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.MTD_DOCTOR?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.MTD_SERIAL_NO?.toString().includes(searchTerm) ||
    item.PatientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const averageTransaction = data.length > 0 ? totals.grandTotal / data.length : 0;
  const maxTransaction = data.reduce((max, item) => {
    const total = (item.MTD_AMOUNT || 0) + (item.Drugs?.reduce((s, d) => s + (d.MDD_AMOUNT || 0), 0) || 0);
    return Math.max(max, total);
  }, 0);

  const bestDay = dailyData.reduce((max, day) => day.total > max.total ? day : max, { total: 0, date: 'N/A' });

  // PDF Download Function with logo
  const downloadPDF = async () => {
    if (!reportRef.current || data.length === 0) return;
    
    try {
      setDownloading(true);
      
      // Create new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add Logo (if loaded)
      if (logoBase64) {
        try {
          // Add logo at top left
          pdf.addImage(logoBase64, 'PNG', 14, 8, 30, 15);
          
          // Calculate center position for the text
          const pageWidth = pdf.internal.pageSize.width;
          const text = 'Aura Meditation';
          
          // Set font to measure text width
          pdf.setFontSize(16);
          pdf.setFont("helvetica", "bold");
          
          // Get text width
          const textWidth = pdf.getTextWidth(text);
          
          // Calculate center X position
          const centerX = (pageWidth - textWidth) / 2;
          
          // Add centered company name
          pdf.setTextColor(25, 118, 210);
          pdf.text(text, centerX, 16);
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      } else {
        // If no logo, just add title
        pdf.setFontSize(18);
        pdf.setTextColor(25, 118, 210);
        pdf.setFont("helvetica", "bold");
        pdf.text(' Aura Meditation', 14, 20);
      }

      // Add Report Title
      pdf.setFontSize(14);
      pdf.setTextColor(33, 33, 33);
      pdf.setFont("helvetica", "bold");
      pdf.text('SALES REPORT', 14, logoBase64 ? 30 : 30);
      
      // Add Report Period with dd/MM/yyyy format
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Period: ${formatDate(startDate)} - ${formatDate(endDate)}`, 14, logoBase64 ? 35 : 35);
      pdf.text(`Generated: ${formatDate(new Date().toISOString())} ${new Date().toLocaleTimeString('en-LK')}`, 14, logoBase64 ? 40 : 40);

      // Add horizontal line
      pdf.setDrawColor(200, 200, 200);
      pdf.line(14, logoBase64 ? 45 : 45, 196, logoBase64 ? 45 : 45);

      // Summary Section
      pdf.setFontSize(12);
      pdf.setTextColor(33, 33, 33);
      pdf.setFont("helvetica", "bold");
      pdf.text('Executive Summary', 14, logoBase64 ? 52 : 52);
      
      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      let yPos = logoBase64 ? 60 : 60;
      
      // Summary boxes
      pdf.setFillColor(240, 240, 240);
      pdf.roundedRect(14, yPos - 2, 85, 25, 2, 2, 'F');
      pdf.roundedRect(105, yPos - 2, 85, 25, 2, 2, 'F');
      
      pdf.setTextColor(33, 33, 33);
      pdf.setFont("helvetica", "bold");
      pdf.text('Revenue Summary', 18, yPos + 2);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Treatment: Rs. ${totals.treatment.toLocaleString('en-LK')}`, 18, yPos + 8);
      pdf.text(`Services: Rs. ${totals.drugs.toLocaleString('en-LK')}`, 18, yPos + 14);
      pdf.text(`Grand Total: Rs. ${totals.grandTotal.toLocaleString('en-LK')}`, 18, yPos + 20);
      
      pdf.setTextColor(33, 33, 33);
      pdf.setFont("helvetica", "bold");
      pdf.text('Transaction Stats', 109, yPos + 2);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(80, 80, 80);
      pdf.text(`Average: Rs. ${averageTransaction.toLocaleString('en-LK')}`, 109, yPos + 8);
      pdf.text(`Highest: Rs. ${maxTransaction.toLocaleString('en-LK')}`, 109, yPos + 14);
      pdf.text(`Treatment: ${((totals.treatment / totals.grandTotal) * 100).toFixed(1)}% | Services: ${((totals.drugs / totals.grandTotal) * 100).toFixed(1)}%`, 109, yPos + 20);

      // All Patients Data Table
      yPos = logoBase64 ? 95 : 95;
      pdf.setFontSize(12);
      pdf.setTextColor(33, 33, 33);
      pdf.setFont("helvetica", "bold");
      pdf.text('Patient Transactions', 14, yPos);

      // Prepare table data with formatted dates
      const tableColumn = [
        { header: 'Date', dataKey: 'date' },
        { header: 'Patient Code', dataKey: 'code' },
        { header: 'Patient Name', dataKey: 'name' },
        { header: 'Treatment (Rs.)', dataKey: 'treatment' },
        { header: 'Services (Rs.)', dataKey: 'services' },
        { header: 'Total (Rs.)', dataKey: 'total' }
      ];
      
      const tableRows = data.map((item) => {
        const drugAmount = item.Drugs?.reduce((s, d) => s + (d.MDD_AMOUNT || 0), 0) || 0;
        const total = (item.MTD_AMOUNT || 0) + drugAmount;
        
        return {
          date: formatDate(item.MTD_DATE),
          code: item.MTD_PATIENT_CODE || 'N/A',
          name: item.PatientName || 'N/A',
          treatment: `Rs. ${(item.MTD_AMOUNT || 0).toLocaleString('en-LK')}`,
          services: `Rs. ${drugAmount.toLocaleString('en-LK')}`,
          total: `Rs. ${total.toLocaleString('en-LK')}`
        };
      });

      // Add table to PDF
      autoTable(pdf, {
        columns: tableColumn,
        body: tableRows,
        startY: yPos + 5,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 3,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
          valign: 'middle',
        },
        headStyles: {
          fillColor: [25, 118, 210],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center',
        },
        bodyStyles: {
          textColor: [50, 50, 50],
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 40, halign: 'left' },
          3: { cellWidth: 28, halign: 'right' },
          4: { cellWidth: 28, halign: 'right' },
          5: { cellWidth: 28, halign: 'right' },
        },
        margin: { left: 14, right: 14 },
        didDrawPage: (data) => {
          // Add footer on each page
          const pageCount = pdf.internal.getNumberOfPages();
          for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            
            // Add footer line
            pdf.setDrawColor(200, 200, 200);
            pdf.line(14, pdf.internal.pageSize.height - 15, 196, pdf.internal.pageSize.height - 15);
            
            // Add page number
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);
            pdf.text(
              `Page ${i} of ${pageCount}`,
              pdf.internal.pageSize.width / 2,
              pdf.internal.pageSize.height - 8,
              { align: 'center' }
            );
            
            // Add generation date on left with dd/MM/yyyy format
            pdf.text(
              `Generated: ${formatDate(new Date().toISOString())}`,
              14,
              pdf.internal.pageSize.height - 8
            );
            
            // Add confidential stamp on right
            pdf.setFont("helvetica", "italic");
            pdf.text(
              'Confidential',
              pdf.internal.pageSize.width - 30,
              pdf.internal.pageSize.height - 8
            );
          }
        },
      });

      // Add summary footer on last page
      const finalY = pdf.lastAutoTable.finalY || 150;
      
      if (finalY < pdf.internal.pageSize.height - 40) {
        pdf.setPage(pdf.internal.getNumberOfPages());
        
        // Add total summary box
        pdf.setFillColor(240, 247, 255);
        pdf.roundedRect(14, finalY + 10, 90, 20, 2, 2, 'F');
        
        pdf.setFontSize(9);
        pdf.setTextColor(25, 118, 210);
        pdf.setFont("helvetica", "bold");
        pdf.text('Total Patients:', 18, finalY + 18);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(33, 33, 33);
        pdf.text(data.length.toString(), 48, finalY + 18);
        
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(25, 118, 210);
        pdf.text('Total Revenue:', 18, finalY + 25);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(33, 33, 33);
        pdf.text(`Rs. ${totals.grandTotal.toLocaleString('en-LK')}`, 48, finalY + 25);
      }

      // Save PDF
      pdf.save(`sales-report-${formatDate(startDate).replace(/\//g, '-')}-to-${formatDate(endDate).replace(/\//g, '-')}.pdf`);
      
    } catch (error) {
      console.error('PDF generation failed:', error);
      setError('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f0f2f5', minHeight: '100vh', p: 3 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              {/* Logo in UI */}
              <Avatar 
                src={logo} 
                alt="Logo" 
                sx={{ width: 70, height: 70, bgcolor: 'white', p: 0.5, border: '1px solid #ddd' }}
              />
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                   Sales Analytics
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <MuiTooltip title="Refresh">
                <IconButton onClick={fetchReport} disabled={loading}>
                  <Refresh />
                </IconButton>
              </MuiTooltip>
              <MuiTooltip title="Download PDF Report">
                <IconButton 
                  onClick={downloadPDF} 
                  disabled={downloading || data.length === 0}
                  color="primary"
                >
                  {downloading ? <CircularProgress size={24} /> : <PictureAsPdf />}
                </IconButton>
              </MuiTooltip>
            </Box>
          </Box>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={handleStartDateChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday />
                    </InputAdornment>
                  ),
                }}
                // Display the formatted date as a helper text
                helperText={startDateDisplay ? `Selected: ${startDateDisplay}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={handleEndDateChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange />
                    </InputAdornment>
                  ),
                }}
                // Display the formatted date as a helper text
                helperText={endDateDisplay ? `Selected: ${endDateDisplay}` : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={fetchReport}
                disabled={loading}
                startIcon={<FilterList />}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && data.length > 0 && (
          <Box ref={reportRef}>
            {/* KPI Cards */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Total Treatment"
                  value={totals.treatment}
                  icon={<LocalHospital />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Total Services"
                  value={totals.drugs}
                  icon={<Medication />}
                  color="#2e7d32"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Grand Total"
                  value={totals.grandTotal}
                  icon={<AttachMoney />}
                  color="#ed6c02"
                />
              </Grid>
            </Grid>

            {/* Statistics Row */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Grid container spacing={3} justifyContent="space-around">
                <Grid item xs={6} md={3}>
                  <StatBadge 
                    label="Average Transaction" 
                    value={formatCompactCurrency(averageTransaction)}
                    color={theme.palette.primary.main}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBadge 
                    label="Highest Transaction" 
                    value={formatCompactCurrency(maxTransaction)}
                    color={theme.palette.success.main}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBadge 
                    label="Treatment %" 
                    value={`${((totals.treatment / totals.grandTotal) * 100).toFixed(1)}%`}
                    color={theme.palette.info.main}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <StatBadge 
                    label="Services %" 
                    value={`${((totals.drugs / totals.grandTotal) * 100).toFixed(1)}%`}
                    color={theme.palette.warning.main}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Charts Section */}
            <Grid>
              {/* Revenue Trend Chart */}
              <Grid item xs={12} md={7.5}>
                <Paper sx={{ p: 2, borderRadius: 2 , marginBottom:'10px'}}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight="bold">
                      Revenue Trend
                    </Typography>
                    <Chip 
                      label={`${dailyData.length} days`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </Box>
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => value}
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) => `Rs.${(value/1000).toFixed(0)}K`}
                          width={50}
                        />
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ fontSize: 12 }}
                        />
                        <Legend 
                          iconType="circle"
                          wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="treatment" 
                          stackId="1"
                          stroke="#1976d2" 
                          fill="#1976d2" 
                          fillOpacity={0.6}
                          name="Treatment"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="drugs" 
                          stackId="1"
                          stroke="#2e7d32" 
                          fill="#2e7d32" 
                          fillOpacity={0.6}
                          name="Services"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={1.5}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">Best Day</Typography>
                      <Typography variant="body1" fontWeight="bold">{bestDay.date}</Typography>
                      <Typography variant="body2" color="primary">{formatCompactCurrency(bestDay.total)}</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2" color="textSecondary">Average Daily</Typography>
                      <Typography variant="body1" fontWeight="bold">{formatCompactCurrency(totals.grandTotal / dailyData.length)}</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Revenue Distribution Chart */}
              <Grid item xs={12} md={4.5}>
                <Paper sx={{ p: 2, borderRadius: 2, height: '100%',marginBottom:'10px' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Revenue Distribution
                  </Typography>
                  <Box sx={{ height: 240, display: 'flex', alignItems: 'center' }}>
                    <ResponsiveContainer width="55%" height="100%">
                      <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <Box sx={{ width: '45%', pl: 1 }}>
                      <Stack spacing={1.5}>
                        {pieData.map((item, index) => (
                          <Box key={item.name}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Box sx={{ width: 10, height: 10, bgcolor: COLORS[index], borderRadius: '50%' }} />
                              <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                            </Box>
                            <Box sx={{ ml: 1.5 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {formatCompactCurrency(item.value)}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {((item.value / totals.grandTotal) * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Transactions Table */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Transactions ({filteredData.length} of {data.length} total)
                </Typography>
                <TextField
                  placeholder="Search..."
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterList />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 250 }}
                />
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>                     
                      <TableCell>Date</TableCell>
                      <TableCell>Patient Code</TableCell>
                      <TableCell>Patient Name</TableCell>                      
                      <TableCell >Treatment (Rs.)</TableCell>
                      <TableCell >Services (Rs.)</TableCell>
                      <TableCell align="right">Total (Rs.)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item) => {
                        const drugAmount = item.Drugs?.reduce((s, d) => s + (d.MDD_AMOUNT || 0), 0) || 0;
                        const total = (item.MTD_AMOUNT || 0) + drugAmount;
                        return (
                          <TableRow key={item.MTD_SERIAL_NO} hover>                            
                            <TableCell>{formatDate(item.MTD_DATE)}</TableCell>
                            <TableCell>{item.MTD_PATIENT_CODE}</TableCell>
                            <TableCell>{item.PatientName || 'N/A'}</TableCell>                            
                            <TableCell >{item.MTD_AMOUNT?.toLocaleString('en-LK') || 0}</TableCell>
                            <TableCell >{drugAmount.toLocaleString('en-LK')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {total.toLocaleString('en-LK')}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
}